import * as dateFns from 'date-fns'
import {useEffect, useMemo, useRef} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {createSelector} from 'reselect'

import {useAppDispatch, useAppSelector} from '../../../../../hooks/react-redux'
import useMyProfile from '../../../../../hooks/useMyProfile'
import {useSetUserProperty} from '../../../../../hooks/useSetUserProperty'
import {useUserProperty} from '../../../../../hooks/useUserProperty'
import {
  isAllPipelinesScreen,
  isPipelinesEditScreen,
  isPipelinesRunScreen,
  isPipelinesScreen,
} from '../../../../../routes'
import {UserProperties} from '../../../../../utils/userProperties'
import {useEditPipeline, useYAMLHasChanges} from '../../EditPipelinePage/EditPipelinePage.hooks'
import {usePipelineId} from '../../hooks/pipeline'

import {feedbackForm, ShowFeedbackFormReasons} from './Feedback.slice'

const THREE_MONTHS = 3

const submittedProjectListSelector = createSelector([(property: string) => property], property =>
  property.split(',').filter(id => id !== ''),
)
export const useSubmittedProjectList = () => {
  const value = useUserProperty(UserProperties.PIPELINES_FEEDBACK_SUBMITTED_PROJECT_LIST) ?? ''
  return submittedProjectListSelector(value)
}

const useShouldShowAfterTimeout = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isAllPipelines = isAllPipelinesScreen(location.pathname)

  const user = useMyProfile()
  const hasNameAndEmail = user?.name != null && user?.email != null

  const today = useMemo(() => new Date(), [])

  const startedUsingPipelinesDateTime = useUserProperty(
    UserProperties.HAS_STARTED_USING_PIPELINES_DATETIME,
  )
  const submittedPipelinesDateTime = useUserProperty(
    UserProperties.PIPELINES_FOLLOW_UP_FEEDBACK_SUBMITTED_DATETIME,
  )
  const setUserProperty = useSetUserProperty()

  const started = useMemo(() => {
    let result = dateFns.parseISO(startedUsingPipelinesDateTime ?? '')
    if (result instanceof Date && isNaN(result.getMilliseconds())) {
      result = today
      setUserProperty(UserProperties.HAS_STARTED_USING_PIPELINES_DATETIME, dateFns.formatISO(today))
    }

    return result
  }, [setUserProperty, startedUsingPipelinesDateTime, today])

  const submittedLast = useMemo(
    () => dateFns.parseISO(submittedPipelinesDateTime ?? ''),
    [submittedPipelinesDateTime],
  )

  useEffect(() => {
    if (isAllPipelines && hasNameAndEmail) {
      const usingPipelinesForOneWeek =
        !submittedPipelinesDateTime && dateFns.differenceInWeeks(today, started) > 0

      const usingPipelinesForOneMonth =
        dateFns.differenceInMonths(today, started) >= 1 &&
        dateFns.differenceInMonths(submittedLast, started) < 1

      const usingPipelinesForThreeMonths =
        dateFns.differenceInMonths(today, started) >= THREE_MONTHS &&
        dateFns.differenceInMonths(submittedLast, started) < THREE_MONTHS

      if (usingPipelinesForOneWeek || usingPipelinesForOneMonth || usingPipelinesForThreeMonths) {
        dispatch(
          feedbackForm.actions.setVisibility({
            visible: true,
            reason: ShowFeedbackFormReasons.FOLLOW_UP,
          }),
        )
      }
    }
  }, [
    dispatch,
    isAllPipelines,
    hasNameAndEmail,
    started,
    submittedLast,
    submittedPipelinesDateTime,
    today,
  ])
}

/* eslint-disable @typescript-eslint/no-magic-numbers */
const MINUTE = 60 * 1000
const STUCK_TIMEOUT = 5 * MINUTE
/* eslint-enable @typescript-eslint/no-magic-numbers */

const useShouldShowIfStuck = () => {
  const dispatch = useAppDispatch()
  const timer = useRef<null | number>(null)
  const location = useLocation()

  const user = useMyProfile()
  const hasNameAndEmail = user?.name != null && user?.email != null

  const id = usePipelineId()
  const submittedProjects = useSubmittedProjectList()
  const isEditingPipelines = isPipelinesEditScreen(location.pathname)

  const editPipeline = useEditPipeline()
  const setUserProperty = useSetUserProperty()
  const yamlHasChanges = useYAMLHasChanges()

  const hasChanges = yamlHasChanges || editPipeline.hasChanges

  useEffect(() => {
    if (isEditingPipelines && hasNameAndEmail) {
      timer.current = window.setTimeout(() => {
        dispatch(
          feedbackForm.actions.setVisibility({
            visible: true,
            reason: ShowFeedbackFormReasons.STUCK,
          }),
        )
      }, STUCK_TIMEOUT)
    }

    if (hasChanges) {
      timer.current != null && window.clearTimeout(timer.current)
    }

    return () => {
      timer.current != null && window.clearTimeout(timer.current)
    }
  }, [
    dispatch,
    hasChanges,
    id,
    hasNameAndEmail,
    isEditingPipelines,
    setUserProperty,
    submittedProjects,
  ])
}

const useShouldHideIfPageChange = () => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const params = useParams()
  const {reason, visible} = useAppSelector(state => state.pipelines.feedbackForm.visibility)
  const isAllPipelines = isPipelinesScreen(location.pathname)
  const isEditingPipelines = isPipelinesEditScreen(location.pathname)
  const isRunningPipelines = isPipelinesRunScreen(location.pathname, params)

  useEffect(() => {
    if (visible) {
      const leftPipelinesPage = !isAllPipelines && reason === ShowFeedbackFormReasons.FOLLOW_UP
      const leftEditingPage = !isEditingPipelines && reason === ShowFeedbackFormReasons.STUCK
      const leftRunningPage = !isRunningPipelines && reason === ShowFeedbackFormReasons.SAVE_AND_RUN

      if (leftPipelinesPage || leftEditingPage || leftRunningPage) {
        dispatch(feedbackForm.actions.setVisibility({visible: false}))
      }
    }
  }, [dispatch, isEditingPipelines, isAllPipelines, isRunningPipelines, reason, visible])
}

export const useShouldShowFeedbackForm = () => {
  useShouldShowAfterTimeout()
  useShouldShowIfStuck()
  useShouldHideIfPageChange()

  return useAppSelector(state => state.pipelines.feedbackForm.visibility)
}
